import { createTheme } from '@mui/material/styles';
import { ELoginState } from '@interfaces/App.interface';
import _ from 'lodash';
import { getSetting } from '@utils/helpers';
import Stores from '@gral/app';
import { generateShades } from './paletteGenerator';

const colors = require('@mui/material/colors');
const { cxStore } = Stores;

const appPalette = {
  primary: '#201b50',
  secondary: '#077bc0',
};

const defaultPalette = {
  palette: { ...appPalette },
};

const updateThemeVariables = newColors => {
  Object.entries(newColors).forEach(([key, value]) => {
    document.documentElement.style.setProperty(`--color-${key}`, value as string);
  });
};

const CustomTheme = (store, lng) => {
  //#region Functions
  // First we are going to map the colours we get from the database/settings,
  // so we can introduce them in materialUI
  const buildingTheme = () => {
    let finalTheme: {
      [key: string]: any;
    } = { ...defaultPalette };

    if (store.getState().global.loginState === ELoginState.LOGGED_IN) {
      const colourSet = store.getState().global.settings?.colourSet?.colourSet ?? _.get(getSetting('colourSet'), 'colourSet'); //We need remove settings from store

      if (colourSet) {
        const newColourSet: {
          [key: string]: string;
        } = {};

        Object.keys(colourSet).map(key => {
          if (colourSet[key].charAt(0) !== '#') {
            const { word, num } = splitWordNumber(colourSet[key]);

            if (num) {
              newColourSet[key] = colors[word] && colors[word][num];
            } else {
              newColourSet[key] = getColorDictionary(word);
            }
          } else {
            newColourSet[key] = colourSet[key];
          }
        });

        const settingsTheme = { palette: newColourSet };

        let primaryColors = {};
        try {
          if (newColourSet.primary) primaryColors = generateShades(newColourSet.primary);
        } catch (e) {
          console.error('Error generating primary colors shades:', e);
        }
        let secondaryColors = {};
        try {
          if (newColourSet.secondary) secondaryColors = generateShades(newColourSet.secondary);
        } catch (e) {
          console.error('Error generating secondary colors shades:', e);
        }

        if (newColourSet.primary1Color) {
          updateThemeVariables({
            'primary-main': newColourSet.primary1Color,
            'primary-1000': newColourSet.primary1Color,
          });
          finalTheme.palette.primary = newColourSet.primary1Color;
        }
        if (newColourSet.primary) {
          updateThemeVariables({
            'primary-main': newColourSet.primary,
            'primary-1000': newColourSet.primary,
          });
          finalTheme.palette.primary = newColourSet.primary;
        }

        for (const tone in primaryColors) {
          updateThemeVariables({
            [`primary-${tone}`]: primaryColors[tone],
          });
        }

        for (const tone in secondaryColors) {
          updateThemeVariables({
            [`secondary-${tone}`]: secondaryColors[tone],
          });
        }

        _.merge(finalTheme, settingsTheme);
      }
    } else {
      let primaryColors = {};
      try {
        if (appPalette.primary) primaryColors = generateShades(appPalette.primary);
      } catch (e) {
        console.error('Error generating primary colors shades:', e);
      }

      let secondaryColors = {};
      try {
        if (appPalette.secondary) secondaryColors = generateShades(appPalette.secondary);
      } catch (e) {
        console.error('Error generating secondary colors shades:', e);
      }

      updateThemeVariables({
        'primary-main': appPalette.primary,
      });

      for (const tone in primaryColors) {
        updateThemeVariables({
          [`primary-${tone}`]: primaryColors[tone],
        });

        for (const tone in secondaryColors) {
          updateThemeVariables({
            [`secondary-${tone}`]: secondaryColors[tone],
          });
        }
      }
    }

    let isRtl = lng == 'ar';
    if (isRtl) {
      const rtlTheme = {
        direction: 'rtl',
        typography: {
          fontSize: finalTheme.typography ? finalTheme.typography.fontSize : 14,
        },
      };
      _.merge(finalTheme, rtlTheme);
    }

    return buildMaterialUITheme(finalTheme);
  };

  // We map the setting data in the ui material theme
  const buildMaterialUITheme = theme => {
    return createTheme({
      direction: theme.direction ?? 'ltr',
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 960,
          lg: 1280,
          xl: 1920,
        },
      },
      typography: {
        fontSize: theme.typography ? theme.typography.fontSize : 12,
      },
      palette: {
        type: 'light',
        primary: {
          main: theme.palette.primary,
          light: colors.grey[100],
          contrastText: '#fff',
        },
        secondary: {
          main: '#fff',
          light: colors.grey[100],
          contrastText: '#000',
        },
        success: {
          main: '#66bb6a',
          light: '#e5f8e5',
          contrastText: '#1e4620',
        },
        warning: {
          main: '#ffa726',
          light: '#f9f8d5',
          contrastText: '#663c00',
        },
        warningLight: {
          main: '#f6ff00',
          light: '#FCFF9E',
          contrastText: '#545700',
        },
        error: {
          main: '#ef5350',
          light: '#fadfdf',
          contrastText: '#5f2120',
          // @ts-ignore
          mainContrastText: '#fff',
        },
        background: {
          paper: '#fff',
        },
        types: {
          light: {
            text: {
              primary: '#000',
            },
            background: {
              paper: '#fff',
              default: '#fff',
              appBar: theme.palette.primary,
              contentFrame: '#fff',
              chip: '#fff',
              avatar: '#fff',
            },
          },
        },
      },
      //components: _themesOverwriteComponents(theme),
    });
  };

  // We overwrite some styles in specific components
  const themesOverwriteComponents = theme => {
    const components = {
      MuiCssBaseline: {
        styleOverrides: themeParam => ({
          '&::-webkit-scrollbar': {
            width: '12px',
            height: '12px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#fff',
            '&:hover': {
              backgroundColor: '#b2dfdb6e',
            },
          },
          '&::-webkit-scrollbar-thumb': {
            border: '2px solid #fff',
            borderRadius: '6px',
            backgroundColor: '#bdb8b899',
            '&:hover': {
              backgroundColor: themeParam.palette?.primary.main,
            },
          },
          //body: themeParam.palette.mode === 'dark' ? darkScrollbar() : null,
        }),
      },
      tabs: {
        backgroundColor: '#fff',
        textColor: '#000',
        selectedTextColor: '#201b50',
      },
      inkBar: { backgroundColor: '#201b50' },
      appBar: { color: '#201b50' },
    };
    if (cxStore.isSuperadmin()) {
      components.appBar = { ...components.appBar, color: '#AA0000' };
    }

    return components;
  };

  const splitWordNumber = word => {
    const matches = word.match(/([a-zA-Z]+)(\d+)/);

    if (matches) {
      return {
        word: matches[1],
        num: parseInt(matches[2]),
      };
    } else {
      return { word, num: undefined };
    }
  };

  const getColorDictionary = word => {
    const dictionary = {
      darkBlack: '#121212',
      fullBlack: '#000',
      white: '#fff',
      black: '#000',
    };
    return dictionary[word];
  };

  //#endregion

  return buildingTheme();
};

export { appPalette, defaultPalette, CustomTheme };
